import type { XieziSquare } from "./xiezi-square.js";
import type { XieziPies } from "./xiezi-pies.js";
import type { NamedCharacter } from "./xiezi-stage-words.js";
import { replay, showStage } from "./xiezi-stage-line.js";
import {
  type DrillProgress,
  cuesNext,
  hidesCharacter,
  isFinished,
  outlineState,
  stageOf,
} from "../handwriting/xiezi-stages.js";

/**
 * Putting where the reader is onto the page.
 *
 * The drill holds the progress and the square holds the drawing, and this is
 * the one place the first is read onto the second: which stage is named, which
 * buttons show, whether the square takes ink, whether the outline shows, how
 * many strokes stand written and which is pointed out, how full the pies are,
 * and whether the character is hidden from the rest of the page. The line
 * under the square is cleared, since a move is what a note was about.
 * `xiezi-drill.ts` calls it after every move.
 *
 * **The character is hidden by an attribute on the block.** From the third
 * stage the reader writes from memory, and `data-cb-xiezi-recall` is what the
 * stylesheet hides the stroke figure over a 词典 entry's drill by.
 * `modules/cidian-xiezi` has the rule.
 *
 * **What draws the eye is animated.** A stage the reader has not been in yet
 * with this character, a note under the square and the drill finished each set
 * an attribute the stylesheet animates, taken off and put back so the same
 * animation plays again. `modules/cidian-xiezi` has the animations.
 */

/** The pieces of one block the drill drives. */
export interface DrillParts {
  readonly block: HTMLElement;
  readonly square: XieziSquare;
  readonly stage: HTMLElement;
  readonly note: HTMLElement;
  readonly check: HTMLButtonElement;
  readonly again: HTMLButtonElement;

  /**
   * Undo and Clear, which a finished drill hides, leaving Again the only
   * button. Absent where the markup drew neither.
   */
  readonly undo?: HTMLButtonElement | undefined;
  readonly clear?: HTMLButtonElement | undefined;

  /** The pies over the square, where the markup made room for them. */
  readonly pies?: XieziPies | undefined;
}

/**
 * Congratulate the reader over the square, once a whole Exercise is done.
 * @param line - The line over the square.
 * @param said - The congratulation.
 */
export function showFinished(line: HTMLElement, said: string): void {
  const strong = line.ownerDocument.createElement("strong");

  strong.className = "cidian-xiezi-task";
  strong.textContent = said;
  line.replaceChildren(strong);
  replay(line, "data-cb-xiezi-done");
}

/**
 * Whether a line under the square is about something done right or wrong.
 * The stylesheet colours it by this, green or red, and a line that is
 * neither keeps the text's own colour.
 */
export type NoteVerdict = "right" | "wrong";

/**
 * Say something under the square, with an animation that catches the eye.
 * @param parts - The block's pieces.
 * @param said - The line.
 * @param verdict - Whether it marks something right or wrong, where it does.
 */
export function showNote(
  parts: Pick<DrillParts, "note">,
  said: string,
  verdict?: NoteVerdict,
): void {
  parts.note.textContent = said;
  setVerdict(parts.note, verdict);
  replay(parts.note, "data-cb-xiezi-said");
}

/** Put a verdict on the note as `data-cb-xiezi-verdict`, or take it off. */
function setVerdict(note: HTMLElement, verdict: NoteVerdict | undefined): void {
  if (verdict === undefined) {
    delete note.dataset["cbXieziVerdict"];
  } else {
    note.dataset["cbXieziVerdict"] = verdict;
  }
}

/**
 * Show where the reader is.
 *
 * @param parts - The block's pieces.
 * @param progress - Where the reader is.
 * @param named - The character being written, and how to describe it.
 * @param done - What the move that brought the reader here finished, which
 * the pies flash.
 */
export function showProgress(
  parts: DrillParts,
  progress: DrillProgress,
  named: NamedCharacter,
  done?: "pass" | "stage",
): void {
  const { square, check, again, undo, clear } = parts;
  const stage = stageOf(progress);
  const finished = isFinished(progress);

  showStage(parts.stage, progress, named);
  parts.note.textContent = "";
  setVerdict(parts.note, undefined);
  parts.block.toggleAttribute("data-cb-xiezi-recall", hidesCharacter(progress));
  parts.pies?.show(progress, done);
  again.hidden = !finished;
  undo?.toggleAttribute("hidden", finished);
  clear?.toggleAttribute("hidden", finished);
  check.hidden = stage !== "free";
  square.lock(finished);
  square.showOutline(outlineState(progress));
  square.showWritten(finished ? square.strokeCount : progress.stroke);
  square.cue(
    stage !== undefined && cuesNext(stage) ? progress.stroke : undefined,
  );
}
